/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "../MinimalSelect";
import FlexBox, { FlexGrow } from "../FlexBox";
import theme from "../../styles/theme";
import { StyledText } from "../Text";
import Button from "../Button";
import { RightAlign } from "../UtilsComponents";
import Icon, { ClickableIcon } from "../Icon";
import Status from "../../types/enum/Status";
import Modal from "../Modal";
import SnackBar from "../SnackBar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TextField } from "@mui/material";
import dayjs from "dayjs";

const sharedMinimalStyle = css`
  width: 100%;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 12px;
  .react-select__menu {
    width: 100%;
    min-width: unset;
    margin-left: -12px;
  }
`;
const StyledMinimalSelect = styled(MinimalSelect)`
  ${sharedMinimalStyle}
`;
type EditAliasForm = {
  source_program: string;
  activity_date: string;
  alias_program: string;
  alias_course_code: string;
  expected_graduation_term: string;
  expected_graduation_date: string;
};
type CourseEditModalProps = {
  reloadParent: (message: string) => void;
  isOpen?: boolean;
  setOpen?: (x: boolean) => void;
  modalData: any;
  setModalData: (x: any) => void;
};

const EditStudentProgramModal = ({
  reloadParent,
  isOpen,
  setOpen,
  modalData,
  setModalData,
}: CourseEditModalProps): JSX.Element => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<EditAliasForm>({});
  const screenClass = useScreenClass();
  const isMobile = useMemo(() => ["xs", "sm"].includes(screenClass), [
    screenClass,
  ]);
  const [loading, setLoading] = useState<boolean>(true);

  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false);
  const [programs, setPrograms] = useState([
    {
      label: "",
      value: "",
    },
  ]);
  const [terms, setTerms] = useState([
    {
      label: "",
      value: "",
    },
  ]);

  const [honors, setHonors] = useState([
    {
      label: "",
      value: "",
    },
  ]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [isValidDate, setIsValidDate] = useState(true);

  useEffect(() => {
    fetchPrograms();
    fetchTerms();
    fetchHonors();
  }, []);
  const fetchPrograms = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/programs")
      .then((res) => {
        if (res.data.data.programs) {
          // eslint-disable-next-line max-len
          const programList: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          res.data.data.programs.forEach(
            (element: { name: string; id: string }) => {
              const array = { label: "", value: "" };
              array.label = element.name;
              array.value = element.id;
              programList.push(array);
            }
          );
          setPrograms(programList);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };
  const onSubmitClicked = (data: any) => {
    setLoading(true);
    axios
      .put(`/api/v1/students/programs/edit`, modalData.data)
      .then((res: any) => {
        setLoading(false);
        reloadParent(res.data.data);
      })
      .catch((error) => {
        setErrorMessage(error?.response?.data?.error);
        setErrorSnackbarOpen(true);
        setLoading(false);
      });
  };
  const onError = (errors: any, e: any) => console.log("error", errors, e);

  const fetchTerms = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/terms")
      .then((res) => {
        if (res.data.data.terms) {
          const term: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          res.data.data.terms.forEach(
            (element: { name: string; id: string }) => {
              const array = { label: "", value: "" };
              array.label = element.name;
              array.value = element.id;
              term.push(array);
            }
          );
          setTerms(term);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };
  const fetchHonors = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/honors")
      .then((res) => {
        if (res.data.data.honors) {
          const honor: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          res.data.data.honors.forEach(
            (element: { honor_name: string; id: string }) => {
              const array = { label: "", value: "" };
              array.label = element.honor_name;
              array.value = element.id;
              honor.push(array);
            }
          );
          setHonors(honor);
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const rawDate_exp_grad_date = modalData.data.expected_graduation_date;
  
  // to change 01-01-0001 as empty
  const pickerValue =
    rawDate_exp_grad_date &&
    dayjs(rawDate_exp_grad_date).isValid() &&
    dayjs(rawDate_exp_grad_date).year() !== 1
      ? dayjs.utc(rawDate_exp_grad_date)
      : null; 
      
  return (
    <>
      <SnackBar
        isOpen={errorSnackbarOpen}
        setOpen={setErrorSnackbarOpen}
        type="error"
        message={errorMessage}
      />
      <Modal
        isOpen={!!isOpen}
        setOpen={setOpen}
        customStyle={{
          content: { width: "100%", maxWidth: isMobile ? "100%" : "800px" },
        }}
      >
        <FlexBox
          style={{
            marginBottom: theme.spacing.base * 2,
          }}
        >
          <FlexGrow>
            <h3 style={{ marginRight: "auto", marginBottom: 0 }}>
              Edit Program
            </h3>
          </FlexGrow>
          <RightAlign>
            <ClickableIcon
              name="x-s"
              onClick={() =>
                setModalData({
                  type: "",
                  data: {
                    student_id: modalData?.data?.student_id,
                    program_id: "",
                    activity_date: new Date().toISOString(),
                    activity_type_id: 4,
                    expected_graduation_date: "",
                    expected_graduation_term_id: "",
                    honor_id: "",
                  },
                })
              }
            />
          </RightAlign>
        </FlexBox>

        {!loading ? (
          <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
                marginTop: 10,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Program
                </StyledText>
              </FlexGrow>
              <FlexGrow style={{ fontWeight: "bold" }} grow={2}>
                <>
                  {programs?.find(
                    (p: any) => p.value === modalData?.data?.program_id
                  )?.label || ""}
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Enrolled Date
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      // disabled
                      //   openTo="year"
                      inputFormat="DD-MM-YYYY"
                      views={["year", "month", "day"]}
                      label="date, month and year"
                      value={dayjs.utc(modalData.data.enrolled_date)}
                      onChange={(newValue) => {
                        if (newValue && dayjs(newValue).isValid()) {
                          const formattedDate = dayjs(newValue)
                            .endOf("day")
                            .toISOString();
                          setModalData({
                            type: "edit",
                            data: {
                              ...modalData.data,
                              enrolled_date: formattedDate,
                            },
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField fullWidth {...params} helperText={null} />
                      )}
                    />
                  </LocalizationProvider>
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Expected Graduation Date
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      //   openTo="year"
                      inputFormat="DD-MM-YYYY"
                      // views={["day", "month", "year"]}
                      label="date, month and year"
                      value={pickerValue}
                      onChange={(newValue) => {
                        setIsValidDate(dayjs(newValue).isValid());
                        if (newValue && dayjs(newValue).isValid()) {
                          const formattedDate = dayjs(newValue)?.endOf("day")?.toISOString();
                          setModalData({
                            type: "edit",
                            data: {
                              ...modalData.data,
                              expected_graduation_date: formattedDate,
                            },
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField fullWidth {...params} helperText={null} />
                      )}
                    />
                  </LocalizationProvider>
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Expected Graduation Term
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <StyledMinimalSelect
                    // multi
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select Term..."
                    options={terms}
                    onChange={(e: any) => {
                      setModalData({
                        type: "edit",
                        data: {
                          ...modalData.data,
                          expected_graduation_term_id: e.value, // Save the formatted date
                        },
                      });
                    }}
                    value={terms.find(
                      (t: any) =>
                        t.value === modalData?.data?.expected_graduation_term_id
                    )}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Honor
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <StyledMinimalSelect
                    // multi
                    // menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Select Honor Status..."
                    options={honors}
                    onChange={(e: any) => {
                      setModalData({
                        type: "edit",
                        data: {
                          ...modalData.data,
                          honor_id: e.value, // Save the formatted date
                        },
                      });
                    }}
                    value={honors.find(
                      (t: any) => t.value === modalData?.data?.honor_id
                    )}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Thesis Title
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <TextField value={modalData?.data?.thesis_title || ""} fullWidth onChange={(e: any) => {
                      setModalData({
                        type: "edit",
                        data: {
                          ...modalData.data,
                          thesis_title: e.target.value,
                        },
                      });
                    }}/>
                </>
              </FlexGrow>
            </FlexBox>

            {modalData?.data?.thesis_title && <FlexBox
                          alignItems="center"
                          style={{
                            marginBottom: theme.spacing.x2,
                          }}
                        >
                          <FlexGrow grow={1}>
                            <StyledText color={"#000000"} fontWeight={"bold"}>
                              Thesis Completed
                            </StyledText>
                          </FlexGrow>
                          <FlexGrow grow={2}>
                            <FlexBox alignItems="center">
                              <input
                                type="checkbox"
                                checked={modalData?.data?.thesis_completed || false}
                                onChange={(e) => {
                                  setModalData({
                                    type: "edit",
                                    data: {
                                      ...modalData.data,
                                      thesis_completed: e.target.checked,
                                    },
                                  });
                                }}
                                style={{ marginRight: theme.spacing.base }}
                              />
                              <StyledText color={"#000000"}>
                                {modalData?.data?.thesis_completed ? "Yes" : "No"}
                              </StyledText>
                            </FlexBox>
                          </FlexGrow>
                        </FlexBox>}
            
                        {modalData?.data?.thesis_completed&& modalData?.data?.thesis_title &&  <FlexBox
                          alignItems="center"
                          style={{
                            marginBottom: theme.spacing.x2,
                          }}
                        >
                          <FlexGrow grow={1}>
                            <StyledText color={"#000000"} fontWeight={"bold"}>
                              Thesis Completed Date
                            </StyledText>
                          </FlexGrow>
                          <FlexGrow grow={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                inputFormat="DD-MM-YYYY"
                                label="date, month and year"
                                value={modalData?.data?.thesis_completed_date || dayjs()}
                                onChange={(newValue) => {
                                  if (newValue && dayjs(newValue).isValid()) {
                                    const formattedDate = dayjs(newValue).toISOString();
                                    setModalData({
                                      type: "edit",
                                      data: {
                                        ...modalData.data,
                                        thesis_completed_date: formattedDate,
                                      },
                                    });
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField fullWidth {...params} helperText={null} />
                                )}
                              />
                            </LocalizationProvider>
                          </FlexGrow>
                        </FlexBox>}

            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  On Probation
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <FlexBox alignItems="center">
                  <input
                    type="checkbox"
                    checked={modalData?.data?.on_probation || false}
                    onChange={(e) => {
                      setModalData({
                        type: "edit",
                        data: {
                          ...modalData.data,
                          on_probation: e.target.checked,
                        },
                      });
                    }}
                    style={{ marginRight: theme.spacing.base }}
                  />
                  <StyledText color={"#000000"}>
                    {modalData?.data?.on_probation ? "Yes" : "No"}
                  </StyledText>
                </FlexBox>
              </FlexGrow>
            </FlexBox>

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <RightAlign>
                  <Button
                    type="button" // Explicitly set type to "button"
                    onClick={() => {
                      setModalData({
                        type: "",
                        data: {
                          student_id: modalData?.data?.student_id,
                          program_id: "",
                          activity_date: new Date().toISOString(),
                          activity_type_id: 4,
                          expected_graduation_date: "",
                          expected_graduation_term_id: "",
                          honor_id: "",
                        },
                      });
                    }}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 30px" }}
                  >
                    <Icon name="x-s" mr={0.5} />
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      //   !modalData?.data?.program_id ||
                      !isValidDate ||
                      !modalData?.data?.expected_graduation_date ||
                      !modalData?.data?.expected_graduation_term_id ||
                      !modalData?.data?.activity_type_id ||
                      !modalData?.data?.enrolled_date || //enromment date
                      !modalData?.data?.student_id
                    }
                    onClick={() => handleSubmit(onSubmitClicked)}
                    status={Status.primary}
                    backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginLeft: "8px" }}
                  >
                    <Icon name="check-mark" mr={0.5} />
                    Save Changes
                  </Button>
                </RightAlign>
              </FlexGrow>
            </FlexBox>
          </form>
        ) : (
          <Icon mr={1} name="spinner" spin />
        )}
      </Modal>
    </>
  );
};

export default EditStudentProgramModal;
